import React from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FloatFilter from "./sections/FloatFilter";
import PatternFilter from "./sections/PatternFilter";
import CheckboxSection from "./sections/CheckboxSection";
import WeaponTypesSection from "./sections/WeaponTypesSection";
import { closeFilter } from "../../../../store/slices/sidebarFilterSlice";
import { useDispatch } from "react-redux";
import {
  EXTRA,
  TYPE,
  UNIQUE,
  PHASE,
  RARITY,
  RARE_PATTERN,
  PISTOLS,
  SMG,
  RIFLES,
  SNIPERS,
  SHOTGUNS,
  MACHINEGUNS,
  EQUIPMENT,
  KNIVES,
} from "../../../items/filterData";


export type InventoryFilterState = {
  name: string;
  tradLockDays: number | null;
  exterior: string[];
  extra: string[];
  type: string[];
  pistols: string[];
  smg: string[];
  rifles: string[];
  snipers: string[];
  shotguns: string[];
  machineguns: string[];
  knives: string[];
  equipment: string[];
  unique: string[];
  phase: string[];
  rarity: string[];
  rarePattern: string[];
  minPrice: number;
  maxPrice: number;
  floatMin: number;
  floatMax: number;
  paintSeed: number | null;
  minFade: number;
  maxFade: number;
  minBlue: number;
  maxBlue: number;
};

export const defaultFilterState: InventoryFilterState = {
  name: "",
  tradLockDays: 0,
  exterior: [],
  extra: [],
  type: [],
  pistols: [],
  smg: [],
  rifles: [],
  snipers: [],
  shotguns: [],
  machineguns: [],
  knives: [],
  equipment: [],
  unique: [],
  phase: [],
  rarity: [],
  rarePattern: [],
  minPrice: 0,
  maxPrice: 100000,
  floatMin: 0,
  floatMax: 1,
  paintSeed: null,
  minFade: 80,
  maxFade: 100,
  minBlue: 0,
  maxBlue: 100,
};

type Props = {
  filters: InventoryFilterState;
  onChange: (f: InventoryFilterState) => void;
  collapsed: boolean;
  onClose: () => void;
};

const SkinDbFilter: React.FC<Props> = ({
                                            filters,
                                            onChange,
                                            collapsed,
                                            onClose,
                                          }) => {
  const dispatch = useDispatch();

  if (collapsed) return null;

  const setField = (key: keyof InventoryFilterState, v: any) =>
      onChange({ ...filters, [key]: v });

  const toggle = (key: keyof InventoryFilterState, value: string) => {
    const arr = filters[key] as string[];
    const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];

    onChange({ ...filters, [key]: next });
  };

  const setList = (key: keyof InventoryFilterState, values: string[]) => {
    onChange({ ...filters, [key]: values });
  };

  return (
      <Box className="bg-neutral-900/70 lg:rounded-xl rounded-none md:h-[88vh] h-[100vh] overflow-y-auto [&::-webkit-scrollbar]:hidden scrollbar-none">

        <div className="flex justify-between mb-2.5 pl-4">
          <div className="mt-5 font-semibold text-lg text-white">Filters</div>

          <IconButton
              onClick={() => dispatch(closeFilter())}
              sx={{ width: 36, height: 36, borderRadius: 5, padding: 0 }}
          >
            <CloseIcon className="text-gray-300" />
          </IconButton>
        </div>

        <div className="flex flex-col gap-6 p-4 pb-6">

          <FloatFilter
              floatMin={filters.floatMin}
              floatMax={filters.floatMax}
              onChange={(min, max) =>
                  onChange({ ...filters, floatMin: min, floatMax: max })
              }
          />

          <PatternFilter
              paintSeed={filters.paintSeed}
              minFade={filters.minFade}
              maxFade={filters.maxFade}
              minBlue={filters.minBlue}
              maxBlue={filters.maxBlue}
              onChange={(partial) => onChange({ ...filters, ...partial })}
          />

          <CheckboxSection
              title="Rare Pattern"
              list={RARE_PATTERN}
              selected={filters.rarePattern}
              onToggle={(v) => toggle("rarePattern", v)}
              onSet={(vals) => setList("rarePattern", vals)}
          />

          <CheckboxSection
              title="Phase"
              list={PHASE}
              selected={filters.phase}
              onToggle={(v) => toggle("phase", v)}
              onSet={(vals) => setList("phase", vals)}
          />

          <CheckboxSection
              title="Extra"
              list={EXTRA}
              selected={filters.extra}
              onToggle={(v) => toggle("extra", v)}
              onSet={(vals) => setList("extra", vals)}
          />

          <CheckboxSection
              title="Type"
              list={TYPE}
              selected={filters.type}
              onToggle={(v) => toggle("type", v)}
              onSet={(vals) => setList("type", vals)}
          />

          <WeaponTypesSection
              filters={filters}
              toggle={toggle}
              setList={setList}
              pistols={PISTOLS}
              smg={SMG}
              rifles={RIFLES}
              snipers={SNIPERS}
              shotguns={SHOTGUNS}
              machineguns={MACHINEGUNS}
              equipment={EQUIPMENT}
              knives={KNIVES}
          />

          <CheckboxSection
              title="Rarity"
              list={RARITY}
              selected={filters.rarity}
              onToggle={(v) => toggle("rarity", v)}
              onSet={(vals) => setList("rarity", vals)}
          />
        </div>
      </Box>
  );
};

export default SkinDbFilter;
